import { useEffect, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import { Button, Card, FormActions, Input, Select } from '@/components/ui'
import { useCreateProperty, useUpdateProperty } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useFormTelemetry } from '@/hooks/useFormTelemetry'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Property } from '@/types'

// ---------------------------------------------------------------------------
// Schema — basic-info fields only (subset of the full propertySchema)
// ---------------------------------------------------------------------------

const generalPropertySchema = z.object({
  name: z.string().min(1, 'Property name is required'),
  // EP-111 T111-7 — standalone-form-only (Q4: never rendered in the
  // onboarding wizard). Hidden entirely unless settings.condominium_enabled.
  property_type: z.enum(['apartment', 'condominium']).optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  tax_id: z.string().optional(),
  branch_number: z.string().optional(),
  location_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  // z.coerce.number() handles the string→number coercion from <select> elements
  // (DOM selects return strings; valueAsNumber returns NaN for selects in JSDOM)
  billing_day: z.coerce.number().int().min(1).max(28),
  // EP-149 T149-017 (MON-8 decision A/B): nullable "due on day N of the
  // billing period". The blank option's <select> value is the empty string,
  // not absent, so a plain z.coerce.number().optional() would still run the
  // number/min/max checks against '' (Number('') coerces to 0, failing
  // min(1)) instead of treating it as unset. z.preprocess maps '' (and a
  // genuinely missing/null value) to undefined BEFORE the number schema
  // runs, so a blank selection validates as "no day set", not a validation
  // error.
  payment_due_day: z.preprocess(
    (v) => (v === '' || v === undefined || v === null ? undefined : Number(v)),
    z.number().int().min(1).max(31).optional(),
  ),
})

type GeneralPropertyFormData = z.infer<typeof generalPropertySchema>

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PropertyGeneralFieldsProps {
  /**
   * null  → create mode: form starts blank, Save fires POST /api/properties.
   * value → edit mode:   form pre-fills, Save fires PATCH /api/properties/:id.
   */
  property: Property | null
  /** Called with the saved Property after a successful POST or PATCH. */
  onSave: (saved: Property) => void
  /**
   * Optional HTML form id. When set, the <form> element receives this id so an
   * external <button form={formId} type="submit"> (e.g. wizard Save & Continue)
   * can trigger submission without being inside the form DOM tree.
   */
  formId?: string
  /**
   * Optional callback fired whenever this component's dirty state changes.
   * Used by PropertyDetailPage to aggregate per-tab dirty state for the
   * global Save button and dirty-dot badges.
   */
  onDirtyChange?: (isDirty: boolean) => void
  /** Fires with the mutation-pending state so an external Save button (wizard
   *  footer) can show a loading spinner while the save is in flight. */
  onSubmittingChange?: (submitting: boolean) => void
  /**
   * Banner rendered above the first input (task-030: ApplyFromFileButton).
   * Pass null or omit to show nothing.
   */
  prefillBanner?: ReactNode
  /**
   * When provided, resets the RHF form with these values so the fields
   * display the AI-parsed data (task-030 Apply path).
   * Setting this triggers a form.reset() via useEffect.
   */
  prefillDefaults?: Partial<GeneralPropertyFormData>
  /**
   * EP-86 T010 — Streamlined wizard mode (S2-1).
   *
   * false (default): render all sections visible as Cards — the new
   * Essentials / Contact / Tax / Location grouping applies on standalone pages
   * (/properties/new + property detail General tab).
   *
   * true (wizard only — passed by OnboardingWizardPage): render only the
   * Essentials section inline with auto-focus, and collapse the Contact / Tax /
   * Location fields inside an "Advanced options" disclosure that is closed by
   * default. Do NOT piggyback this on `formId` (S2-1 decision).
   */
  streamlinedMode?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Self-contained basic-info form for a property.
 *
 * Covers: name, billing_day (Essentials) plus phone, email, tax_id,
 * branch_number, address, location_url (Advanced — always visible on
 * standalone pages; collapsible in the wizard via streamlinedMode).
 *
 * Owns its own useForm instance and mutation hooks so it can be embedded in
 * any context (PropertyFormPage, PropertyDetailPage general tab, wizard step).
 *
 * No client-side role checks — Pundit enforces authorization on the API side.
 */
export function PropertyGeneralFields({
  property,
  onSave,
  formId,
  onDirtyChange,
  onSubmittingChange,
  prefillBanner,
  prefillDefaults,
  streamlinedMode = false,
  className,
}: PropertyGeneralFieldsProps) {
  const { t } = useTranslation('properties')
  const { t: tc } = useTranslation('common')
  // EP-114 S1-13 — the new billing-day helper notice lives in `onboarding`
  // (it was authored for the wizard copy set even though it now also renders
  // on the standalone pages, per D-2).
  const { t: to } = useTranslation('onboarding')

  const translateError = useApiErrorTranslator('properties')
  const addToast = useNotificationStore((s) => s.addToast)
  // EP-73 Tier-1: form_started:property / form_submitted:property lifecycle.
  const { markStarted, markSubmitted } = useFormTelemetry('property')

  const createProperty = useCreateProperty()
  const updateProperty = useUpdateProperty()

  // EP-111 T111-7 — the canonical single-gate accessor (established by
  // T111-6). No Unleash flag exists for condominium; this is purely an
  // owner-toggled account setting.
  const { data: accountSettings } = useAccountSettings()
  const condominiumEnabled = accountSettings?.settings?.condominium_enabled === true

  const isEdit = property !== null

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<GeneralPropertyFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(generalPropertySchema) as any,
    values: property
      ? {
          name: property.name,
          address: property.address ?? '',
          phone: property.phone ?? '',
          email: property.email ?? '',
          tax_id: property.tax_id ?? '',
          branch_number: property.branch_number ?? '',
          location_url: property.location_url ?? '',
          billing_day: property.billing_day ?? 1,
          // MON-8 decision B: an existing property with no payment_due_day
          // stays blank (the field never invents a default for it) until the
          // operator deliberately picks a day.
          payment_due_day: property.payment_due_day ?? undefined,
          property_type: property.property_type ?? 'apartment',
        }
      : {
          name: '',
          address: '',
          phone: '',
          email: '',
          tax_id: '',
          branch_number: '',
          location_url: '',
          billing_day: 1,
          // MON-8 decision B: a brand-new property defaults to day 5.
          payment_due_day: 5,
          property_type: 'apartment',
        },
  })

  // Notify parent when dirty state changes so PropertyDetailPage can show
  // per-tab dirty-dot badges and control the page-header Save button.
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // When the wizard passes prefillDefaults (Apply from file path), reset the
  // form with those values so the user sees them pre-filled immediately.
  useEffect(() => {
    if (prefillDefaults && Object.keys(prefillDefaults).length > 0) {
      reset((current) => ({ ...current, ...prefillDefaults }))
    }
    // Only re-run when the reference identity of prefillDefaults changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDefaults])

  const onSubmit = async (data: GeneralPropertyFormData) => {
    const payload = {
      name: data.name,
      address: data.address || undefined,
      phone: data.phone || undefined,
      email: data.email || undefined,
      tax_id: data.tax_id || undefined,
      branch_number: data.branch_number || undefined,
      location_url: data.location_url || undefined,
      billing_day: data.billing_day,
      // EP-149 T149-017 (MON-8): always sent, as an explicit `null` when
      // blank rather than omitted. Unlike the optional string fields above
      // (which drop the key via `|| undefined` when empty, leaving an
      // existing value untouched server-side), a blank payment_due_day is a
      // real, meaningful state: an operator who clears a previously-set day
      // back to blank needs that PATCH to actually null the column, not
      // leave the old day in place.
      payment_due_day: data.payment_due_day ?? null,
      // Sent even when the dropdown is hidden — the value stays the property's
      // existing property_type (or 'apartment' for a new property), so this
      // never regresses an already-condominium property when the toggle is off.
      property_type: data.property_type,
    }

    try {
      if (isEdit) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const saved = await updateProperty.mutateAsync({ id: property.id, ...(payload as any) })
        markSubmitted()
        onSave(saved)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const saved = await createProperty.mutateAsync(payload as any)
        markSubmitted()
        onSave(saved)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const isPending = createProperty.isPending || updateProperty.isPending

  // Report mutation-pending so the wizard footer Save can show a spinner.
  useEffect(() => {
    onSubmittingChange?.(isSubmitting || isPending)
  }, [isSubmitting, isPending, onSubmittingChange])

  // ---------------------------------------------------------------------------
  // Shared field groups — used in both layout modes
  // ---------------------------------------------------------------------------

  const billingDayOptions = Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: t('labels.billing_day_option', { day: i + 1 }),
  }))

  // A blank day keeps the issue-date + 15-day rule for existing properties.
  const paymentDueDayOptions = [
    { value: '', label: t('labels.payment_due_day_blank') },
    ...Array.from({ length: 31 }, (_, i) => ({
      value: String(i + 1),
      label: t('labels.payment_due_day_option', { day: i + 1 }),
    })),
  ]

  // EP-111 T111-7 — reuses the leftover property_types i18n keys (predate the
  // EP-58 column drop).
  const propertyTypeOptions = [
    { value: 'apartment', label: t('property_types.apartment') },
    { value: 'condominium', label: t('property_types.condominium') },
  ]

  // Contact sub-group: phone + email
  const contactFields = (
    <div className="grid gap-4 md:grid-cols-2">
      <Input
        label={t('labels.phone')}
        placeholder={t('placeholders.phone')}
        {...register('phone')}
      />
      <Input
        label={t('labels.email')}
        placeholder={t('placeholders.email')}
        type="email"
        error={errors.email?.message}
        {...register('email')}
      />
    </div>
  )

  // Tax sub-group: tax_id + branch_number
  const taxFields = (
    <div className="grid gap-4 md:grid-cols-2">
      <Input
        label={t('labels.tax_id')}
        placeholder={t('placeholders.tax_id')}
        {...register('tax_id')}
      />
      <Input
        label={t('labels.branch_number')}
        placeholder={t('placeholders.branch_number')}
        {...register('branch_number')}
      />
    </div>
  )

  // Location sub-group: address + location_url
  const locationFields = (
    <div className="grid gap-4">
      <Input
        label={t('labels.address')}
        placeholder={t('placeholders.address')}
        {...register('address')}
      />
      <Input
        label={t('labels.location_url')}
        placeholder={t('placeholders.location_url')}
        type="url"
        error={errors.location_url?.message}
        {...register('location_url')}
      />
    </div>
  )

  // EP-114 S1-13 — new helper notice rendered directly under the billing-day
  // field. Shared between both layout modes (D-2 sub-form principle: a
  // purely informational addition lands once in the shared component, not
  // forked per streamlinedMode).
  const billingDayNote = (
    <p data-testid="billing-day-note" className="mt-2 flex items-center gap-1.5 text-xs text-base-content">
      <Info className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>{to('wizard.step_1_billing_day_note')}</span>
    </p>
  )

  // F10/AC-8 (EP-158): the zod schema's `name` message ('Property name is
  // required') is a raw English string, shown verbatim regardless of
  // locale — now that noValidate (above) lets it surface at all. The
  // generic, already-translated "required" message is reused (shared with
  // every other required-field error in the app) rather than adding a
  // field-specific key for the same sentence.
  const nameErrorMessage = errors.name
    ? errors.name.type === 'too_small'
      ? tc('errors.required')
      : errors.name.message
    : undefined

  return (
    <div className={className}>
      {/* Apply-from-file banner (task-030) — rendered above first input */}
      {prefillBanner}
      {/* F10/AC-8 (EP-158): the Input's `required` prop sets the native HTML
          `required` attribute (for the asterisk styling), which made the
          browser intercept an empty submit with its own untranslated bubble
          ("Please fill out this field.") before RHF/Zod ever ran. noValidate
          hands validation entirely back to the form's own (translated)
          error messages below. */}
      <form id={formId} onSubmit={handleSubmit(onSubmit)} onChangeCapture={markStarted} className="space-y-6" noValidate>

        {streamlinedMode ? (
          // -----------------------------------------------------------------
          // Wizard mode (streamlinedMode=true): ESSENTIALS ONLY — name +
          // billing_day (Figma 86-4526). The optional Contact / Tax / Location
          // fields are intentionally omitted from onboarding to keep step 1
          // minimal; they stay editable later on the property detail page.
          // -----------------------------------------------------------------
          <div className="space-y-4">
            <Input
              label={t('labels.property_name')}
              placeholder={t('placeholders.property_name')}
              error={nameErrorMessage}
              required
              autoFocus
              // EP-114 S1-11/S1-12 — opt-in overrides only; Input's own
              // defaults are untouched (D-0b).
              inputClassName="rounded-lg h-11 px-4 py-3"
              asteriskClassName="text-fd-negative"
              data-tour-target="general-property-name"
              {...register('name')}
            />
            <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
              <Select
                label={t('labels.billing_day')}
                required
                options={billingDayOptions}
                // EP-114 S1-11: opt-in override only; Select's own defaults
                // are untouched (D-0b).
                selectClassName="rounded-lg h-11 px-4 py-3"
                {...register('billing_day')}
              />
              {/* EP-149 T149-017 (MON-8) */}
              <Select
                label={t('labels.payment_due_day')}
                error={errors.payment_due_day?.message}
                options={paymentDueDayOptions}
                value={watch('payment_due_day') ?? ''}
                selectClassName="rounded-lg h-11 px-4 py-3"
                {...register('payment_due_day')}
              />
            </div>
            {billingDayNote}
          </div>
        ) : (
          // -----------------------------------------------------------------
          // Standalone mode (streamlinedMode=false — default):
          //   All sections visible, grouped into Cards. The Essentials card
          //   holds name + billing_day; three separate Cards cover the
          //   Advanced sub-groups (Contact, Tax, Location).
          // -----------------------------------------------------------------
          <>
            {/* Essentials card: name + billing_day */}
            <Card title={t('sections.essentials')}>
              <div className="grid gap-4">
                <Input
                  label={t('labels.property_name')}
                  placeholder={t('placeholders.property_name')}
                  error={nameErrorMessage}
                  required
                  // EP-114 S1-11/S1-12 — opt-in overrides only; Input's own
                  // defaults are untouched (D-0b). Applied here too (not just
                  // the wizard) per the D-2 sub-form principle.
                  inputClassName="rounded-lg h-11 px-4 py-3"
                  asteriskClassName="text-fd-negative"
                  data-tour-target="general-property-name"
                  {...register('name')}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2 mt-4 [&>*]:min-w-0">
                <Select
                  label={t('labels.billing_day')}
                  required
                  options={billingDayOptions}
                  // EP-114 S1-11 — opt-in override only; Select's own
                  // defaults are untouched (D-0b).
                  selectClassName="rounded-lg h-11 px-4 py-3"
                  {...register('billing_day')}
                />
                {/* EP-149 T149-017 (MON-8) */}
                <Select
                  label={t('labels.payment_due_day')}
                  error={errors.payment_due_day?.message}
                  options={paymentDueDayOptions}
                  value={watch('payment_due_day') ?? ''}
                  selectClassName="rounded-lg h-11 px-4 py-3"
                  {...register('payment_due_day')}
                />
                {/* EP-111 T111-7 — standalone-form-only (Q4): rendered ONLY when
                    settings.condominium_enabled. Never shown in the onboarding
                    wizard (streamlinedMode branch above has no property_type field). */}
                {condominiumEnabled && (
                  <Select
                    label={t('labels.property_type')}
                    options={propertyTypeOptions}
                    {...register('property_type')}
                  />
                )}
              </div>
              {billingDayNote}
              <p data-testid="billing-settings-hint" className="text-sm text-base-content/60 mt-3">
                {t('sections.billing_settings_hint')}
              </p>
            </Card>

            {/* Contact card: phone + email */}
            <Card title={t('sections.contact')}>
              {contactFields}
            </Card>

            {/* Tax card: tax_id + branch_number */}
            <Card title={t('sections.tax')}>
              {taxFields}
            </Card>

            {/* Location card: address + location_url */}
            <Card title={t('sections.location')}>
              {locationFields}
            </Card>
          </>
        )}

        {/* Submit — hidden in wizard mode. When the wizard passes a `formId`
            prop, the OnboardingBottomBar's "Save & Continue" button submits
            this form via `<button form="<formId>" type="submit">`. The
            in-form button was a duplicate that confused users. */}
        {!formId && (
          <FormActions>
            {/* Disabled when pristine so accidental taps on mobile don't trigger
                empty-form submissions. isDirty becomes true on the first field change. */}
            <Button type="submit" loading={isSubmitting || isPending} disabled={!isDirty}>
              {isEdit ? tc('actions.save_changes') : t('actions.add_property')}
            </Button>
          </FormActions>
        )}
      </form>
    </div>
  )
}
